import FetchClient, { type ApiResponse } from "./FetchClient";
import { getTokensFromStorage } from "./AuthService";

export interface AlertProduct {
  upc?: string;
  name?: string;
  imageUrl?: string;
  store?: string;
  storeId?: string;
  retailer?: string;
  oldPrice?: number;
  newPrice?: number;
  oldStock?: number;
  newStock?: number;
}

export interface Announcement {
  type: string;
  createdAt: number;
  title: string;
  body: string;
  titleEs?: string;
  bodyEs?: string;
  imageUrl?: string;
  link?: string;
  popUp?: boolean;
  createdBy?: string;
  // Alert-specific fields
  userId?: string;
  alertHash?: string;
  alertReason?: string; // price_drop, back_in_stock, price_drop_back_in_stock
  alertProduct?: AlertProduct;
}

export interface PaginatedAnnouncementsResponse {
  announcements: Announcement[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface UnseenAnnouncementsResponse {
  announcements: Announcement[];
  count: number;
}

export interface UnseenAlertsResponse {
  alerts: Announcement[];
  count: number;
}

// Admin endpoints
export async function getAdminAnnouncements(limit = 20, cursor?: string): Promise<PaginatedAnnouncementsResponse> {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (cursor) params.append("cursor", cursor);
  const response = await FetchClient.get<ApiResponse<PaginatedAnnouncementsResponse>>(`/admin/announcements?${params}`);
  return response.data;
}

export async function createAnnouncement(data: { title: string; body: string; titleEs?: string; bodyEs?: string; imageUrl?: string; link?: string; popUp?: boolean }): Promise<Announcement> {
  const response = await FetchClient.post<ApiResponse<Announcement>>("/admin/announcements", data);
  return response.data;
}

export async function updateAnnouncement(createdAt: number, data: { title: string; body: string; titleEs?: string; bodyEs?: string; imageUrl?: string; link?: string; popUp?: boolean }): Promise<Announcement> {
  const response = await FetchClient.patch<ApiResponse<Announcement>>(`/admin/announcements/${createdAt}`, data);
  return response.data;
}

export async function deleteAnnouncement(createdAt: number): Promise<void> {
  await FetchClient.delete(`/admin/announcements/${createdAt}`);
}

export async function uploadAnnouncementImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("image", file);
  const tokens = getTokensFromStorage();

  const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/admin/announcements/upload-image`, {
    method: "POST",
    headers: {
      Authorization: tokens?.accessToken || "",
      "X-Source": "web",
    },
    body: formData,
  });

  if (!response.ok) throw new Error("Failed to upload image");
  const json = await response.json();
  return json.data.imageUrl;
}

// User endpoints
export async function getAnnouncements(limit = 10, cursor?: string): Promise<PaginatedAnnouncementsResponse> {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (cursor) params.append("cursor", cursor);
  const response = await FetchClient.get<ApiResponse<PaginatedAnnouncementsResponse>>(`/announcements?${params}`);
  return response.data;
}

export async function getLatestAnnouncement(): Promise<Announcement | null> {
  const response = await FetchClient.get<ApiResponse<Announcement | null>>("/announcements/latest");
  return response.data;
}

export async function getUnseenAnnouncements(): Promise<UnseenAnnouncementsResponse> {
  const response = await FetchClient.get<ApiResponse<UnseenAnnouncementsResponse>>("/announcements/unseen");
  return response.data;
}

export async function markAnnouncementsSeen(): Promise<{ lastSeenAnnouncementAt: number }> {
  const response = await FetchClient.post<ApiResponse<{ lastSeenAnnouncementAt: number }>>("/announcements/mark-seen", {});
  return response.data;
}

// Alert endpoints (product-specific alerts)
export async function getUserAlerts(limit = 20, cursor?: string): Promise<PaginatedAnnouncementsResponse> {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (cursor) params.append("cursor", cursor);
  const response = await FetchClient.get<ApiResponse<PaginatedAnnouncementsResponse>>(`/alerts?${params}`);
  return response.data;
}

export async function getUnseenAlerts(): Promise<UnseenAlertsResponse> {
  const response = await FetchClient.get<ApiResponse<UnseenAlertsResponse>>("/alerts/unseen");
  return response.data;
}

export async function markAlertsSeen(): Promise<{ lastSeenAlertAt: number }> {
  const response = await FetchClient.post<ApiResponse<{ lastSeenAlertAt: number }>>("/alerts/mark-seen", {});
  return response.data;
}

export async function deleteAlert(createdAt: number): Promise<void> {
  await FetchClient.delete(`/alerts/${createdAt}`);
}

export async function deleteAllAlerts(): Promise<{ deleted: number }> {
  const response = await FetchClient.delete<ApiResponse<{ deleted: number }>>("/alerts");
  return response.data;
}
